import type {
	Adapter,
	AnalyticsEvents,
	SocialMediaType,
} from "../../AppCommunicator";
import { LinkTypes } from "../../AppCommunicator";
import { sendToFlutter } from "./AdapterUtil";

enum FlutterEvents {
	FIRST_TIME_CLICKED = "firstTimeClicked",
	LOADING_DONE = "loadingDone",
	CHANGE_LOADING = "changeLoading",
	PLAY_REWARDED_AD = "playRewarded",
	ANALYTICS_EVENT = "analyticsEvent",
	BUY_PRODUCT = "buyProduct",
	SET_LANGUAGE = "setLanguage",
	OPEN_STORE = "openStoreReview",
	CAN_VIBRATE = "canVibrate",
	VIBRATE = "vibrate",
	EXIT_APP = "exitApp",
	DOWNLOAD_IMAGE = "downloadImage",
	LINK_ACCOUNT = "linkAccount",
	RESET_GAME = "resetGame",
	RELOG_USER = "relogUser",
	GAME_VERSION = "game_version",
	OPEN_SECOND_WEBVIEW = "openSecondWebview",
	CLOSE_SECOND_WEBVIEW = "closeSecondWebview",
	SHOW_PRIVACY_OPTIONS_FORM_UMP = "showPrivacyOptionsFormUmp",
	SHOW_ERROR_DURING_LOADING = "showErrorDuringLoading",
}

export class FlutterAdapter implements Adapter {
	firstTimeClicked(): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.FIRST_TIME_CLICKED }),
		);
	}

	loadingDone(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.LOADING_DONE }));
	}

	setLoadingProgress(progress: number): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.CHANGE_LOADING,
				loadingValue: progress,
			}),
		);
	}

	playRewardedAd(): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.PLAY_REWARDED_AD }),
		);
	}

	sendAnalyticsEvent(
		event: AnalyticsEvents,
		data?: Record<string, number | string>,
	): void {
		const record = data || {};
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.ANALYTICS_EVENT,
				eventName: event,
				...record,
			}),
		);
	}

	buyProduct(productId: string): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.BUY_PRODUCT, productId }),
		);
	}

	setLanguage(language: string): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.SET_LANGUAGE, language }),
		);
	}

	openStore(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.OPEN_STORE }));
	}

	canVibrate(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.CAN_VIBRATE }));
	}

	vibrate(vibrationTimeMs: number): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.VIBRATE,
				timeMs: vibrationTimeMs,
			}),
		);
	}

	exitApp(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.EXIT_APP }));
	}

	downloadImage(base64: string): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.DOWNLOAD_IMAGE,
				base64Image: base64,
			}),
		);
	}

	linkAccount(type: LinkTypes, socialMediaType: SocialMediaType): void {
		if (type === LinkTypes.WITH_EXIST) {
			sendToFlutter(
				JSON.stringify({
					event: FlutterEvents.LINK_ACCOUNT,
					withExist: true,
					socialMediaType: socialMediaType,
				}),
			);
			return;
		} else if (type === LinkTypes.WITH_OVERRIDE) {
			sendToFlutter(
				JSON.stringify({
					event: FlutterEvents.LINK_ACCOUNT,
					withOverride: true,
					socialMediaType: socialMediaType,
				}),
			);
			return;
		} else {
			sendToFlutter(
				JSON.stringify({
					event: FlutterEvents.LINK_ACCOUNT,
					socialMediaType: socialMediaType,
				}),
			);
		}
	}

	resetGame(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.RESET_GAME }));
	}

	relogUser(): void {
		sendToFlutter(JSON.stringify({ event: FlutterEvents.RELOG_USER }));
	}

	sendGameVersion(version: string): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.GAME_VERSION,
				value: version,
			}),
		);
	}

	openSecondWebview(url: string): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.OPEN_SECOND_WEBVIEW, url }),
		);
	}

	closeSecondWebview(): void {
		sendToFlutter(
			JSON.stringify({ event: FlutterEvents.CLOSE_SECOND_WEBVIEW }),
		);
	}

	showPrivacyOptionsFormUmp(): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.SHOW_PRIVACY_OPTIONS_FORM_UMP,
			}),
		);
	}

	public showErrorDuringLoading(errorText: string = "Unknown error"): void {
		sendToFlutter(
			JSON.stringify({
				event: FlutterEvents.SHOW_ERROR_DURING_LOADING,
				errorText,
			}),
		);
	}
}
